import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Skeleton,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import AuthContext from '../../context/AuthContext';
import { authService, profileService } from '../../services/api';
import AppLayout from '../layout/AppLayout';
import { INK, MUTED, RULE, Section } from '../analytics/analyticsUi';
import TwoFactorSection from './TwoFactorSection';

const formatDate = (value, withTime = false) =>
  value
    ? new Date(value).toLocaleString('en-IN', withTime
      ? { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }
      : { day: 'numeric', month: 'long', year: 'numeric' })
    : '–';

const PasswordField = ({ label, name, value, onChange, autoComplete, helperText, error }) => {
  const [visible, setVisible] = useState(false);
  return (
    <TextField
      fullWidth
      size="small"
      type={visible ? 'text' : 'password'}
      label={label}
      name={name}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
      helperText={helperText}
      error={error}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={() => setVisible((v) => !v)}
              aria-label={visible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
              edge="end"
            >
              {visible ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
            </IconButton>
          </InputAdornment>
        )
      }}
    />
  );
};

const Profile = () => {
  const { user, updateUser } = useContext(AuthContext);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState({ name: '', email: '', company: '', phone: '', gstin: '' });
  const [savingDetails, setSavingDetails] = useState(false);

  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [savingPassword, setSavingPassword] = useState(false);

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Load the full profile (login only returns a few fields)
  useEffect(() => {
    const load = async () => {
      try {
        const response = await authService.getCurrentUser();
        const data = response.data || user || {};
        setProfile(data);
        setDetails({ name: data.name || '', email: data.email || '', company: data.company || '', phone: data.phone || '', gstin: data.gstin || '' });
      } catch (err) {
        setError('Your profile could not be loaded. Refresh the page to try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const detailsChanged = profile && ['name', 'email', 'company', 'phone', 'gstin'].some((f) => (details[f] || '') !== (profile[f] || ''));
  const passwordTooShort = passwords.newPassword.length > 0 && passwords.newPassword.length < 8;
  const passwordMismatch = passwords.confirmPassword.length > 0 && passwords.newPassword !== passwords.confirmPassword;

  const saveDetails = async (e) => {
    e.preventDefault();
    if (!details.name.trim()) {
      setError('Enter your name.');
      return;
    }
    try {
      setSavingDetails(true);
      const response = await profileService.updateProfile(details);
      setProfile(response.data);
      updateUser?.(response.data);
      setSuccess('Profile saved');
    } catch (err) {
      setError(err.response?.data?.error || 'Your profile could not be saved. Try again.');
    } finally {
      setSavingDetails(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (passwordTooShort || passwordMismatch) return;
    try {
      setSavingPassword(true);
      await profileService.changePassword({
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword
      });
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSuccess('Password changed');
    } catch (err) {
      setError(err.response?.data?.error || 'Your password could not be changed. Try again.');
    } finally {
      setSavingPassword(false);
    }
  };

  const shown = profile || user || {};
  const initial = (shown.name || shown.email || '?').charAt(0).toUpperCase();

  return (
    <AppLayout>
      <Container maxWidth="md" sx={{ pb: 6 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 4 }}>
          <Avatar sx={{ width: 64, height: 64, fontSize: '1.75rem', fontWeight: 600, bgcolor: '#128C7E' }}>
            {initial}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 700, letterSpacing: '-0.025em', color: INK, lineHeight: 1.15 }}
              noWrap
            >
              {loading ? <Skeleton width={220} /> : shown.name}
            </Typography>
            <Typography variant="body2" sx={{ color: MUTED, mt: 0.5 }} noWrap>
              {shown.email}
              {shown.company ? `, ${shown.company}` : ''}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>
        )}

        {/* Details */}
        <Section title="Your details" description="Used on invoices and when our team contacts you.">
          {loading ? (
            <Box sx={{ display: 'grid', gap: 2 }}>
              {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={40} />)}
            </Box>
          ) : (
            <Box component="form" onSubmit={saveDetails} noValidate>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  size="small"
                  label="Full name"
                  name="name"
                  autoComplete="name"
                  required
                  value={details.name}
                  onChange={(e) => setDetails({ ...details, name: e.target.value })}
                />
                <TextField
                  size="small"
                  type="email"
                  label="Email address"
                  name="email"
                  autoComplete="email"
                  required
                  value={details.email}
                  onChange={(e) => setDetails({ ...details, email: e.target.value })}
                />
                <TextField
                  size="small"
                  label="Company"
                  name="company"
                  autoComplete="organization"
                  value={details.company}
                  onChange={(e) => setDetails({ ...details, company: e.target.value })}
                />
                <TextField
                  size="small"
                  type="tel"
                  label="Phone number"
                  name="phone"
                  autoComplete="tel"
                  value={details.phone}
                  onChange={(e) => setDetails({ ...details, phone: e.target.value })}
                />
                <TextField
                  size="small"
                  label="GSTIN (optional)"
                  name="gstin"
                  value={details.gstin}
                  onChange={(e) => setDetails({ ...details, gstin: e.target.value.toUpperCase().replace(/\s/g, '') })}
                  inputProps={{ maxLength: 15 }}
                  helperText="Shown on your invoices"
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.5 }}>
                {detailsChanged && (
                  <Button
                    onClick={() => setDetails({ name: profile.name || '', email: profile.email || '', company: profile.company || '', phone: profile.phone || '', gstin: profile.gstin || '' })}
                    disabled={savingDetails}
                    sx={{ textTransform: 'none' }}
                  >
                    Discard changes
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!detailsChanged || savingDetails}
                  startIcon={savingDetails ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{ textTransform: 'none' }}
                >
                  {savingDetails ? 'Saving…' : 'Save changes'}
                </Button>
              </Box>
            </Box>
          )}
        </Section>

        {/* Password */}
        <Section title="Password" description="Use at least 8 characters. You'll stay signed in on this device.">
          <Box component="form" onSubmit={savePassword} noValidate>
            {/* Lets password managers associate the fields with this account */}
            <input type="text" name="username" autoComplete="username" value={shown.email || ''} readOnly hidden />
            <Box sx={{ display: 'grid', gap: 2, maxWidth: { sm: 420 } }}>
              <PasswordField
                label="Current password"
                name="currentPassword"
                autoComplete="current-password"
                value={passwords.currentPassword}
                onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
              />
              <PasswordField
                label="New password"
                name="newPassword"
                autoComplete="new-password"
                value={passwords.newPassword}
                onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                error={passwordTooShort}
                helperText={passwordTooShort ? 'Use at least 8 characters' : ' '}
              />
              <PasswordField
                label="Confirm new password"
                name="confirmPassword"
                autoComplete="new-password"
                value={passwords.confirmPassword}
                onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                error={passwordMismatch}
                helperText={passwordMismatch ? "The passwords don't match" : ' '}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  savingPassword || !passwords.currentPassword || !passwords.newPassword ||
                  !passwords.confirmPassword || passwordTooShort || passwordMismatch
                }
                startIcon={savingPassword ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ textTransform: 'none' }}
              >
                {savingPassword ? 'Changing…' : 'Change password'}
              </Button>
            </Box>
          </Box>
        </Section>

        <TwoFactorSection email={shown.email} />

        {/* Account */}
        <Section title="Account">
          {[
            { label: 'Role', value: shown.role === 'admin' ? 'Administrator' : 'Customer' },
            { label: 'Member since', value: formatDate(shown.createdAt) },
            { label: 'Last sign-in', value: formatDate(shown.lastLogin, true) }
          ].map((row, index) => (
            <Box
              key={row.label}
              sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 2, py: 1.25, borderTop: index ? `1px solid ${RULE}` : 'none' }}
            >
              <Typography variant="body2" sx={{ color: MUTED }}>{row.label}</Typography>
              <Typography variant="body2" sx={{ color: INK }}>{loading ? <Skeleton width={140} /> : row.value}</Typography>
            </Box>
          ))}
        </Section>

        <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setSuccess(null)} severity="success" variant="filled" sx={{ width: '100%' }}>{success}</Alert>
        </Snackbar>
      </Container>
    </AppLayout>
  );
};

export default Profile;
